import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useDispatch} from 'react-redux';
import {useCallback, useState} from 'react';
import {StyleSheet, View, Text, Pressable} from 'react-native';
import {useToast} from 'react-native-toast-notifications';
import {IconButton} from 'interface/base/IconButton';
import {TextInput} from 'interface/base/Input';
import {uploadAddWebFile} from 'utils/mediafire';
import {colors, fonts, vectors} from 'config/styles';
import {theme} from 'config/themes';

export interface WebUploadProps {
  id: string,
  url?: string,
  exit: () => void,
}

export function ModalWebUpload(props: WebUploadProps) {
  const {t} = useLingui();
  const [stateUrl, setUrl] = useState(props.url || '');
  const [stateName, setName] = useState('');
  const toast = useToast();

  const dispatch = useDispatch();
  const update = useCallback((url) => {
    setUrl(url);
    let name = '';
    try {name = new URL(url).pathname.split('/').pop()} catch(e) {}
    if (name) setName(name);
  }, []);

  const submit = useCallback(async () => {
    const res = await uploadAddWebFile(dispatch, props.id, stateUrl, stateName);
    if (res && res['result'] === 'Success') {
      toast.show(`Uploading “${stateName}” from the web...`);
    } else {
      toast.show(res?.message ?? t`Failed to submit web upload`, {type: 'danger'});
    }
    props.exit();
  }, [props.id, props.url, stateName]);

  return (
    <View style={styles.root}>
      <View style={styles.bar}>
        <Text style={styles.title}>
          {t`Upload from the Web`}
        </Text>
        <Pressable
          style={styles.close}
          onPress={props.exit}>
          <Svg viewBox="0 0 14 14" width={14} height={14}>
            <Path d={vectors.icons.close} fill={colors.neutral.mid}/>
          </Svg>
        </Pressable>
      </View>
      <View style={styles.content}>
        <View style={styles.messageWrapper}>
          <Text style={styles.hint}>
            {'Enter a file URL and name to start your web upload. Web uploads have a maximum file size of '}
            <Text style={styles.hintBold}>
              {'20 GB'}
            </Text>
            {'.'}
          </Text>
          <Text style={styles.message}>
            {t`Enter the file URL:`}
          </Text>
        </View>
        <TextInput
          autoFocus
          placeholder={"https://example.com/photo.jpg"}
          autoCapitalize="none"
          keyboardType="url"
          returnKeyType="send"
          textContentType="URL"
          defaultValue={stateUrl}
          onChangeText={update}
          onSubmitEditing={submit}
        />
        <View style={styles.spacerV}/>
        <View style={styles.messageWrapper}>
          <Text style={styles.message}>
            {t`Choose a file name:`}
          </Text>
        </View>
        <TextInput
          placeholder="photo.jpg"
          defaultValue={stateName}
          onChangeText={setName}
          onSubmitEditing={submit}
        />
      </View>
      <View style={styles.buttonWrapper}>
        <IconButton
          buttonStyle={styles.buttonUpload}
          mode="primary"
          text={t`Upload via web`.toUpperCase()}
          onPress={submit}
        />
        <View style={styles.spacer}/>
        <IconButton
          mode="cancel"
          text={t`Cancel`.toUpperCase()}
          onPress={props.exit}
        />
      </View>
    </View>
  );
}

export const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: theme.dialog.fill,
  },
  content: {
    paddingHorizontal: 20,
  },
  message: {
    fontSize: fonts.sizes.m,
    fontWeight: '500',
    color: theme.dialog.text,
    lineHeight: 24,
    marginBottom: 4,
  },
  messageWrapper: {
  },
  hint: {
    //...fonts.regular,
    fontSize: fonts.sizes.m,
    color: theme.dialog.text,
    marginBottom: 20,
  },
  hintBold: {
    //...fonts.bold,
    fontSize: fonts.sizes.m,
    color: theme.dialog.text,
  },
  buttonWrapper: {
    flexDirection: 'row-reverse',
    padding: 20,
  },
  buttonUpload: {
    paddingHorizontal: 12,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: theme.dialog.input.text,
    backgroundColor: theme.dialog.input.fill,
  },
  inputWrapper: {
    borderBottomWidth: 2,
    borderColor: colors.primary.brand,
    marginBottom: 12,
  },
  spacer: {
    width: 6,
  },
  spacerV: {
    height: 10,
  },
  bar: {
  },
  title: {
    fontSize: 20,
    fontWeight: '300',
    color: theme.dialog.title,
    lineHeight: 65,
    marginLeft: 22,
  },
  close: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    height: 65,
    width: 58,
    right: 1,
    top: -2,
  },
});
